import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import SafeSiteSeoContext from '../context/SafeSiteSeoContext';
import { convertToRelativePath } from "../utils/convertToRelativePath";
import { transformImageUrl } from "./SafeSiteImage";
import { lowestPriceProduct } from '../utils/productHelpers';
import { useProductCategory } from '../context/ProductCategoryContext';

export default function SafeSiteSeo ({ post = {}, meta = [], title, postSchema }) {
  const { global, options } = useContext(SafeSiteSeoContext);

  const { seo } = post;

  const siteUrl = global.siteUrl

  let fullSchema;

  if (postSchema) {
    fullSchema = postSchema;
  } else if (seo && seo?.schema?.raw) {
    fullSchema = JSON.parse(seo.schema.raw);
  }

  if (fullSchema && fullSchema['@graph']) {
    fullSchema['@graph'].forEach((item) => {
      if (item['@type'] === 'WebPage') {
        item.thumbnailUrl =  transformImageUrl({ siteUrl, imageUrl: item.thumbnailUrl })
      }
      if (item['@type'] === 'ImageObject') {
        item.url = transformImageUrl({ siteUrl, imageUrl: item.url })
        item.contentUrl = transformImageUrl({ siteUrl, imageUrl: item.contentUrl })
      }
      if (item['@type'] === 'Organization') {
        item.logo.url = transformImageUrl({ siteUrl, imageUrl: item.logo.url })
        item.logo.contentUrl = transformImageUrl({ siteUrl, imageUrl: item.logo.contentUrl })
      }
      if (item['@type'] === 'BreadcrumbList') {
        if (item['@id']) {
          item['@id'] = item['@id'].startsWith(siteUrl) ? item['@id'] : `${siteUrl}${item['@id']}`
        }
        item.itemListElement.forEach((element) => {
          if (element?.item) {
            element.item = element.item.startsWith(siteUrl) ? element.item : `${siteUrl}${element.item}`
          }
        })
      }
    })
  }

  if (options?.schemaReplacement?.from && options?.schemaReplacement?.to) {
    fullSchema = JSON.stringify(fullSchema).replaceAll(
      options.schemaReplacement.from,
      options.schemaReplacement.to
    );
  } else {
    fullSchema = JSON.stringify(fullSchema);
  }

  const inLanguage = global?.schema?.inLanguage;

  const schema = global?.schema;
  const webmaster = global?.webmaster;
  const social = global?.social;

  const verification = [];

  if (webmaster?.baiduVerify) {
    verification.push({
      name: `baidu-site-verification`,
      content: webmaster.baiduVerify,
    });
  }
  if (webmaster?.googleVerify) {
    verification.push({
      name: `google-site-verification`,
      content: webmaster.googleVerify,
    });
  }
  if (webmaster?.msVerify) {
    verification.push({
      name: `msvalidate.01`,
      content: webmaster.msVerify,
    });
  }
  if (webmaster?.yandexVerify) {
    verification.push({
      name: `yandex-verification`,
      content: webmaster.yandexVerify,
    });
  }
  if (social?.pinterest && social.pinterest.metaTag) {
    verification.push({
      name: `p:domain_verify`,
      content: social.pinterest.metaTag,
    });
  }

  const metaTitle = title || seo?.title;
  const metaDescription = seo?.metaDesc ? seo.metaDesc : '';

  const robotsIndex = seo?.metaRobotsNoindex === 'noindex' ? 'noindex' : 'index';
  const robotsFollow = seo?.metaRobotsNofollow === 'nofollow' ? 'nofollow' : 'follow';

  const getReadingTime = time => (time === 1 ? '1 minute' : `${time} minutes`);

  const ogImage = transformImageUrl({ siteUrl, imageUrl: seo?.opengraphImage?.sourceUrl });
  const twitterImage = transformImageUrl({ siteUrl, imageUrl: seo?.twitterImage?.sourceUrl });

  let canonical = seo?.canonical
  if (canonical && canonical.includes('/graphql/')) {
    canonical = null
  }
  if (canonical && !canonical.includes('https://') && !canonical.includes('http://')) {
    canonical = `${siteUrl}${convertToRelativePath(canonical)}`
  }

  const { products } = useProductCategory();

  const showProductComponents = Array.isArray(products) && products.length > 0;

  return (
    <Helmet
      htmlAttributes={{
        lang: inLanguage,
      }}
      title={metaTitle}
      meta={[
        {
          name: `robots`,
          content: `max-snippet:-1, max-image-preview:large, max-video-preview:-1, ${robotsIndex}, ${robotsFollow}`,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:site_name`,
          content: schema?.companyName,
        },
        {
          property: `og:title`,
          content: seo?.opengraphTitle || metaTitle,
        },
        {
          property: `og:description`,
          content: seo?.opengraphDescription,
        },
        {
          property: `og:locale`,
          content: seo?.schema?.inLanguage ? seo.schema.inLanguage.replaceAll('-', '_') : null,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: 'og:image',
          content: ogImage || seo?.opengraphImage?.sourceUrl || social?.facebook?.defaultImage?.mediaItemUrl,
        },
        {
          property: 'og:image:alt',
          content: seo?.opengraphImage?.altText || social?.facebook?.defaultImage?.altText,
        },
        {
          name: `twitter:card`,
          content: social?.twitter.cardType,
        },
        {
          name: `twitter:creator`,
          content: social?.twitter.username,
        },
        {
          name: `twitter:title`,
          content: seo?.twitterTitle || metaTitle,
        },
        {
          name: `twitter:description`,
          content: seo?.twitterDescription || metaDescription,
        },
        {
          name: 'twitter:image',
          content: twitterImage || seo?.twitterImage?.sourceUrl,
        },
        {
          name: 'twitter:image:alt',
          content: seo?.twitterImage?.altText,
        },
        {
          name: 'twitter:label1',
          content: seo?.readingTime ? 'Estimated reading time' : null,
        },
        {
          name: 'twitter:data1',
          content: seo?.readingTime ? getReadingTime(+seo.readingTime) : '',
        },
      ]
        .filter(m => !!m.content)
        .concat(meta, verification)}
      encodeSpecialCharacters={false}
    >
      {fullSchema && <script type="application/ld+json">{fullSchema}</script>}
      {canonical && <link rel="canonical" href={canonical} />}
      {showProductComponents && <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": [
              ${products.map(
                (product, index) =>
                  `{
                    "@type": "ListItem",
                    "position": ${index + 1},
                    "url": "${siteUrl}${convertToRelativePath(product.uri)}"
                  }`
              ).join(',')}
            ]
          }
        `}
      </script>}
      {showProductComponents && lowestPriceProduct(products, 'hirePrice')?.productData?.productMeta?.hirePrice?.price && (<meta name="custom-hire-price" content={lowestPriceProduct(products, 'hirePrice').productData.productMeta.hirePrice.price} />)}
      {showProductComponents && lowestPriceProduct(products, 'buyPrice')?.productData?.productMeta?.hirePrice?.price && (<meta name="custom-buy-price" content={lowestPriceProduct(products, 'buyPrice').productData.productMeta.buyPrice.price} />)}
    </Helmet>
  );
};